/**
 * AuthContext - Manages authentication state across the app
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTeam, setCurrentTeam] = useState(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const accessToken = localStorage.getItem('access_token');

        if (storedUser && accessToken) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);

          // Set primary team as current team
          if (parsedUser.teams && parsedUser.teams.length > 0) {
            const primaryTeam = parsedUser.teams.find(t => t.is_primary) || parsedUser.teams[0];
            setCurrentTeam(primaryTeam);

            // Store current team ID
            localStorage.setItem('current_team_id', primaryTeam.id.toString());
          }
        }
      } catch (error) {
        console.error('Error loading user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const register = async (email, password, name, inviteCode = null) => {
    try {
      const data = await authAPI.register(email, password, name, inviteCode);
      setUser(data.user);

      // Set current team if user joined via invite
      if (data.user.teams && data.user.teams.length > 0) {
        const primaryTeam = data.user.teams[0];
        setCurrentTeam(primaryTeam);
        localStorage.setItem('current_team_id', primaryTeam.id.toString());
      }

      toast.success(`Welcome, ${data.user.name}!`);
      return data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const data = await authAPI.login(email, password);
      setUser(data.user);

      // Set current team
      if (data.user.teams && data.user.teams.length > 0) {
        const primaryTeam = data.user.teams.find(t => t.is_primary) || data.user.teams[0];
        setCurrentTeam(primaryTeam);
        localStorage.setItem('current_team_id', primaryTeam.id.toString());
      }

      toast.success(`Welcome back, ${data.user.name}!`);
      return data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      setCurrentTeam(null);
      localStorage.removeItem('current_team_id');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear state even if API call fails
      setUser(null);
      setCurrentTeam(null);
      localStorage.removeItem('current_team_id');
    }
  };

  const switchTeam = (teamId) => {
    if (!user || !user.teams) return;

    const team = user.teams.find(t => t.id === teamId);
    if (team) {
      setCurrentTeam(team);
      localStorage.setItem('current_team_id', teamId.toString());
      toast.success(`Switched to ${team.name}`);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      // Update current team if it changed
      if (userData.teams && currentTeam) {
        const updatedCurrentTeam = userData.teams.find(t => t.id === currentTeam.id);
        if (updatedCurrentTeam) {
          setCurrentTeam(updatedCurrentTeam);
        }
      }

      return userData;
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  const googleLogin = async (credential) => {
    try {
      const data = await authAPI.googleLogin(credential);
      setUser(data.user);

      // Set current team
      if (data.user.teams && data.user.teams.length > 0) {
        const primaryTeam = data.user.teams.find(t => t.is_primary) || data.user.teams[0];
        setCurrentTeam(primaryTeam);
        localStorage.setItem('current_team_id', primaryTeam.id.toString());
      }

      toast.success(`Welcome, ${data.user.name}!`);
      return data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Google login failed';
      toast.error(message);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    currentTeam,
    isAuthenticated: !!user,
    isSuperAdmin: user?.is_superadmin || false,
    register,
    login,
    logout,
    switchTeam,
    refreshUser,
    googleLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
